/**
 * User Preferences Store
 *
 * Manages user preferences including:
 * - Theme (light/dark/system)
 * - Language
 * - Notification settings
 *
 * @module @schoolidb/store/preferencesStore
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

// ============================================================================
// Types
// ============================================================================

/**
 * Theme options
 */
export type Theme = 'light' | 'dark' | 'system';

/**
 * Language options
 */
export type Language = 'en' | 'hi' | 'ta' | 'te' | 'bn' | 'mr' | 'gu' | 'kn' | 'ml' | 'pa';

/**
 * Notification preferences
 */
export interface NotificationPreferences {
  /** Email notifications enabled */
  email: boolean;
  /** Push notifications enabled */
  push: boolean;
  /** Marketing emails enabled */
  marketing: boolean;
  /** School updates enabled */
  schoolUpdates: boolean;
  /** Community notifications enabled */
  community: boolean;
}

/**
 * Preferences state
 */
export interface PreferencesState {
  /** Current theme */
  theme: Theme;
  /** Current language */
  language: Language;
  /** Notification preferences */
  notifications: NotificationPreferences;
  /** Reduced motion preference */
  reducedMotion: boolean;
  /** High contrast mode */
  highContrast: boolean;
}

/**
 * Preferences actions
 */
export interface PreferencesActions {
  /** Set theme */
  setTheme: (theme: Theme) => void;
  /** Set language */
  setLanguage: (language: Language) => void;
  /** Update notification preferences */
  setNotifications: (notifications: Partial<NotificationPreferences>) => void;
  /** Set reduced motion */
  setReducedMotion: (enabled: boolean) => void;
  /** Set high contrast */
  setHighContrast: (enabled: boolean) => void;
  /** Reset to defaults */
  resetPreferences: () => void;
}

export type PreferencesStore = PreferencesState & PreferencesActions;

// ============================================================================
// Initial State
// ============================================================================

const initialState: PreferencesState = {
  theme: 'system',
  language: 'en',
  notifications: {
    email: true,
    push: true,
    marketing: false,
    schoolUpdates: true,
    community: true,
  },
  reducedMotion: false,
  highContrast: false,
};

// ============================================================================
// Store
// ============================================================================

/**
 * Preferences store with persistence
 */
export const usePreferencesStore = create<PreferencesStore>()(
  persist(
    (set) => ({
      ...initialState,

      setTheme: (theme) => {
        set({ theme });
      },

      setLanguage: (language) => {
        set({ language });
      },

      setNotifications: (notifications) => {
        set((state) => ({
          notifications: { ...state.notifications, ...notifications },
        }));
      },

      setReducedMotion: (reducedMotion) => {
        set({ reducedMotion });
      },

      setHighContrast: (highContrast) => {
        set({ highContrast });
      },

      resetPreferences: () => {
        set(initialState);
      },
    }),
    {
      name: 'schoolidb-preferences',
      storage: createJSONStorage(() => localStorage),
    }
  )
);

// ============================================================================
// Selectors
// ============================================================================

export const selectTheme = (state: PreferencesStore) => state.theme;
export const selectLanguage = (state: PreferencesStore) => state.language;
export const selectNotifications = (state: PreferencesStore) => state.notifications;
export const selectReducedMotion = (state: PreferencesStore) => state.reducedMotion;
export const selectHighContrast = (state: PreferencesStore) => state.highContrast;
